// src/pages/actividades/components/SubtareasList.tsx
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { PriorityBadge, StatusBadge } from '@/components/ui';
import { useCompletarActividad, useReabrirActividad } from '@/hooks/queries/useActividades';
import type { ActividadListItem } from '@/types';
import { cn } from '@/lib/utils';

interface Props {
  subtareas: ActividadListItem[];
}

export function SubtareasList({ subtareas }: Props) {
  const completar = useCompletarActividad();
  const reabrir = useReabrirActividad();

  const handleCompletar = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await completar.mutateAsync({ id });
  };

  const handleReabrir = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await reabrir.mutateAsync(id);
  };

  if (subtareas.length === 0) {
    return (
      <p className="text-gray-500 text-center py-4">
        No hay subtareas
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {subtareas.map((subtarea) => (
        <div
          key={subtarea.id}
          className={cn(
            'flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors',
            subtarea.estado === 'COMPLETADA' && 'opacity-60'
          )}
        >
          {subtarea.estado !== 'COMPLETADA' && subtarea.estado !== 'CANCELADA' ? (
            <button
              onClick={(e) => handleCompletar(subtarea.id, e)}
              disabled={completar.isPending}
              className="h-5 w-5 rounded border-2 border-gray-300 hover:border-green-500 hover:bg-green-50 flex items-center justify-center transition-colors flex-shrink-0"
            >
              <Check className="h-3 w-3 text-transparent" />
            </button>
          ) : subtarea.estado === 'COMPLETADA' ? (
            <button
              onClick={(e) => handleReabrir(subtarea.id, e)}
              disabled={reabrir.isPending}
              className="h-5 w-5 rounded bg-green-500 hover:bg-green-400 flex items-center justify-center transition-colors flex-shrink-0"
              title="Reabrir subtarea"
            >
              <Check className="h-3 w-3 text-white" />
            </button>
          ) : (
            <div className="h-5 w-5 rounded bg-gray-300 flex items-center justify-center flex-shrink-0">
              <Check className="h-3 w-3 text-white" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <Link
              to={`/actividades/${subtarea.id}`}
              className={cn(
                'font-medium hover:text-blue-600',
                subtarea.estado === 'COMPLETADA' && 'line-through'
              )}
            >
              {subtarea.titulo}
            </Link>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <PriorityBadge priority={subtarea.prioridad} />
            <StatusBadge status={subtarea.estado} />
          </div>
        </div>
      ))}
    </div>
  );
}
